import React, { useState, useEffect } from 'react';
import { COLORS } from '../../utils/constants';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { notificationsAPI } from '../../services/api';
import { Bell, BellOff, Search, Settings, QrCode, X, TrendingUp, Clock, Star, CheckCheck } from 'lucide-react';

const MobileHeader = ({ title, showBack, onBack }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [showSearch, setShowSearch] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    const saved = localStorage.getItem('notificationsEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showToggleToast, setShowToggleToast] = useState(false);

  useEffect(() => {
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadUnreadCount = async () => {
    try {
      const res = await notificationsAPI.getUnreadCount();
      setUnreadCount(res.data?.count || 0);
    } catch (err) {
      // Silently fail
    }
  };

  const toggleNotifications = () => {
    const newValue = !notificationsEnabled;
    setNotificationsEnabled(newValue);
    localStorage.setItem('notificationsEnabled', JSON.stringify(newValue));
    setShowToggleToast(true);
    setTimeout(() => setShowToggleToast(false), 2000);
  };

  // Check if we're on the notifications page
  const isNotificationsPage = location.pathname === '/notifications';

  // Popular search items
  const popularSearches = ['BTC', 'ETH', 'SOL', 'DOGE', 'XRP'];

  // Get page title based on route
  const getTitle = () => {
    if (title) return title;
    switch (location.pathname) {
      case '/': return 'ExDex';
      case '/trading': return t('trading');
      case '/deposits': return t('wallet');
      case '/withdrawals': return t('withdraw');
      case '/profile': return 'ExDex';
      case '/wallet': return 'ExDex';
      case '/ido': return 'ExDex';
      case '/admin': return t('adminPanel');
      default: return 'ExDex';
    }
  };

  // Home page header style
  const isHome = location.pathname === '/';

  if (isHome) {
    return (
      <header
        style={{
          backgroundColor: COLORS.background,
          padding: '12px 16px',
          paddingTop: 'max(12px, env(safe-area-inset-top))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
      >
        {/* Left: Logo and greeting */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: COLORS.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#000',
              fontWeight: 'bold',
              fontSize: '16px',
            }}
          >
            {user?.email?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div>
            <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>Good morning</div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: COLORS.text }}>
              {user?.email?.split('@')[0] || 'Trader'}
            </div>
          </div>
        </div>

        {/* Right: Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={() => setShowSearch(true)}
            style={{
              backgroundColor: COLORS.card,
              border: 'none',
              borderRadius: '10px',
              padding: '10px',
              cursor: 'pointer',
            }}
          >
            <Search size={20} color={COLORS.textSecondary} />
          </button>
          <button
            onClick={() => navigate('/notifications')}
            style={{
              backgroundColor: COLORS.card,
              border: 'none',
              borderRadius: '10px',
              padding: '10px',
              cursor: 'pointer',
              position: 'relative',
            }}
          >
            <Bell size={20} color={COLORS.textSecondary} />
            {unreadCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: '6px',
                  right: '6px',
                  width: unreadCount > 9 ? '16px' : '8px',
                  height: unreadCount > 9 ? '16px' : '8px',
                  backgroundColor: COLORS.danger,
                  borderRadius: '50%',
                  fontSize: '10px',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {unreadCount > 9 ? '9+' : ''}
              </span>
            )}
          </button>
          <button
            onClick={() => navigate('/wallet')}
            style={{
              backgroundColor: COLORS.card,
              border: 'none',
              borderRadius: '10px',
              padding: '10px',
              cursor: 'pointer',
            }}
          >
            <QrCode size={20} color={COLORS.textSecondary} />
          </button>
        </div>

        {/* Search Modal */}
        {showSearch && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: COLORS.background,
            zIndex: 1000,
            padding: '16px',
            paddingTop: 'max(16px, env(safe-area-inset-top))',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                backgroundColor: COLORS.card,
                borderRadius: '10px',
                padding: '12px 16px',
                gap: '12px',
              }}>
                <Search size={20} color={COLORS.textSecondary} />
                <input
                  type="text"
                  placeholder="Поиск монет..."
                  autoFocus
                  style={{
                    flex: 1,
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: COLORS.text,
                    fontSize: '16px',
                    outline: 'none',
                  }}
                />
              </div>
              <button
                onClick={() => setShowSearch(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  padding: '8px',
                  cursor: 'pointer',
                }}
              >
                <X size={24} color={COLORS.textSecondary} />
              </button>
            </div>

            <div style={{ color: COLORS.textSecondary, fontSize: '13px', marginBottom: '12px' }}>Популярные</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {popularSearches.map(coin => (
                <button
                  key={coin}
                  onClick={() => {
                    setShowSearch(false);
                    navigate(`/trading?symbol=${coin}USDT`);
                  }}
                  style={{
                    backgroundColor: COLORS.card,
                    border: 'none',
                    borderRadius: '20px',
                    padding: '8px 16px',
                    color: COLORS.text,
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <Star size={14} color={COLORS.primary} />
                  {coin}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Notifications Modal */}
        {showNotifications && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: COLORS.background,
            zIndex: 1000,
            padding: '16px',
            paddingTop: 'max(16px, env(safe-area-inset-top))',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ color: COLORS.text, fontSize: '20px', fontWeight: '700', margin: 0 }}>Уведомления</h2>
              <button
                onClick={() => setShowNotifications(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  padding: '8px',
                  cursor: 'pointer',
                }}
              >
                <X size={24} color={COLORS.textSecondary} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {notifications.map(notif => (
                <div
                  key={notif.id}
                  style={{
                    backgroundColor: COLORS.card,
                    borderRadius: '12px',
                    padding: '16px',
                    borderLeft: notif.read ? 'none' : `3px solid ${COLORS.primary}`,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                    <span style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600' }}>{notif.title}</span>
                    <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>{notif.time}</span>
                  </div>
                  <p style={{ color: COLORS.textSecondary, fontSize: '13px', margin: 0 }}>{notif.message}</p>
                </div>
              ))}
            </div>

            {notifications.length === 0 && (
              <div style={{ textAlign: 'center', color: COLORS.textSecondary, marginTop: '40px' }}>
                <Bell size={48} color={COLORS.border} />
                <p style={{ marginTop: '16px' }}>Нет уведомлений</p>
              </div>
            )}
          </div>
        )}
      </header>
    );
  }

  // Standard page header
  return (
    <header
      style={{
        backgroundColor: COLORS.background,
        padding: '12px 16px',
        paddingTop: 'max(12px, env(safe-area-inset-top))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        borderBottom: `1px solid ${COLORS.border}`,
      }}
    >
      {/* Title */}
      <h1
        style={{
          fontSize: '20px',
          fontWeight: '700',
          color: COLORS.text,
          margin: 0,
        }}
      >
        {getTitle()}
      </h1>

      {/* Right actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {/* Bell icon - toggle on notifications page, navigate otherwise */}
        <button
          onClick={isNotificationsPage ? toggleNotifications : () => navigate('/notifications')}
          style={{
            backgroundColor: isNotificationsPage && !notificationsEnabled ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
            border: isNotificationsPage && !notificationsEnabled ? '1px solid rgba(239, 68, 68, 0.3)' : 'none',
            borderRadius: '8px',
            padding: '8px',
            cursor: 'pointer',
            position: 'relative',
          }}
        >
          {isNotificationsPage && !notificationsEnabled ? (
            <BellOff size={22} color={COLORS.danger} />
          ) : (
            <Bell size={22} color={COLORS.textSecondary} />
          )}
          {!isNotificationsPage && unreadCount > 0 && (
            <span
              style={{
                position: 'absolute',
                top: '4px',
                right: '4px',
                width: unreadCount > 9 ? '16px' : '8px',
                height: unreadCount > 9 ? '16px' : '8px',
                backgroundColor: COLORS.danger,
                borderRadius: '50%',
                fontSize: '10px',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {unreadCount > 9 ? '9+' : ''}
            </span>
          )}
        </button>
        {location.pathname !== '/profile' && (
          <button
            onClick={() => navigate('/profile')}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              padding: '8px',
              cursor: 'pointer',
            }}
          >
            <Settings size={22} color={COLORS.textSecondary} />
          </button>
        )}
      </div>

      {/* Toast for notifications toggle */}
      {showToggleToast && (
        <div style={{
          position: 'fixed',
          bottom: 100,
          left: '50%',
          transform: 'translateX(-50%)',
          background: notificationsEnabled ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
          border: `1px solid ${notificationsEnabled ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
          borderRadius: 12,
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          zIndex: 1000,
          animation: 'slideUp 0.3s ease-out',
        }}>
          {notificationsEnabled ? (
            <Bell size={20} color="#22c55e" />
          ) : (
            <BellOff size={20} color="#ef4444" />
          )}
          <span style={{ 
            color: notificationsEnabled ? '#22c55e' : '#ef4444', 
            fontSize: 14, 
            fontWeight: 600 
          }}>
            {notificationsEnabled ? 'Уведомления включены' : 'Уведомления отключены'}
          </span>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateX(-50%) translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
    </header>
  );
};

export default MobileHeader;
